// ═══════════════════════════════════════════════════════════════════════════
// ADMIN: STAGES
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Migration témoin (mai 2026) : 100 % des libellés UI passent par t().
// Trois patterns appliqués ici, à reprendre pour migrer les autres
// pages :
//   1. Libellés génériques transversaux → clés `common.*`
//      (common.save, common.delete, common.field.name, …)
//   2. Libellés propres à la page → clés `<domaine>.*` scoped
//      (stages.toast.created, stages.modal.delete.message, …)
//   3. Interpolation : `t('stages.modal.delete.message', { name })`.
//      Le `{name}` est auto-échappé par t() — pas besoin d'esc() côté
//      caller. Le HTML structurel (<strong> ici) reste dans le template.

interface StageEntity {
  id: number;
  name: string;
  position: number;
}

declare global {
  interface Window {
    renderStagesPage: typeof renderStagesPage;
    initStagesDragDrop: typeof initStagesDragDrop;
    showStageModal: typeof showStageModal;
  }
}

// La route est gardée par `stages.read` (boot.ts), qui n'est qu'une clé de
// menu : elle n'autorise pas l'écriture. Sans ce test, les CTA Créer / ✎ / ✕ et
// le glisser-déposer étaient rendus à tout le monde et le serveur répondait 403
// au clic (revue front §I3).
const canWriteStages = (): boolean =>
  !!(window.hasPermission && window.hasPermission('stages.write'));

pages.adminStages = async function () {
  const canWrite = canWriteStages();
  setHeader(
    t('stages.page_title', { plural: t('vocabulary.stage_plural') }),
    canWrite
      ? `<button class="btn btn-secondary ml-auto" id="btn-ei-stages" style="margin-right:6px">${esc(t('common.export_import'))}</button>` +
          `<button class="btn btn-primary" id="btn-new-stage">${esc(t('stages.btn.add'))}</button>`
      : '',
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const stages = (await loadStages(true)) as StageEntity[];
    renderStagesPage(stages);
    document
      .getElementById('btn-new-stage')
      ?.addEventListener('click', () => showStageModal(null, stages));
    document.getElementById('btn-ei-stages')?.addEventListener('click', () => {
      const examples = window.EXPORT_EXAMPLES['stages'];
      window.showExportImportModal(
        'stages',
        t('stages.page_title', { plural: t('vocabulary.stage_plural') }),
        examples?.json ?? '',
        examples?.csv ?? '',
      );
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderStagesPage(stages: StageEntity[]): void {
  const canWrite = canWriteStages();
  const sorted = [...stages].sort((a, b) => a.position - b.position);

  const rows = sorted
    .map(
      (s) => `
    <tr class="stage-drag-row" draggable="${canWrite}" data-stage-id="${s.id}">
      <td style="width:32px;text-align:center">${canWrite ? `<span class="drag-handle" role="button" aria-label="${esc(t('stages.dragdrop.aria_handle'))}">⠿</span>` : ''}</td>
      <td style="cursor:${canWrite ? 'pointer' : 'default'}" ${canWrite ? `data-click-stage="${s.id}"` : ''}><strong>${esc(s.name)}</strong></td>
      <td>${
        canWrite
          ? `<button class="btn btn-secondary btn-icon" data-edit-stage="${s.id}" title="${esc(t('common.edit'))}">✎</button>
        <button class="btn btn-danger btn-icon" data-del-stage="${s.id}" data-stage-name="${esc(s.name)}" style="margin-left:4px" title="${esc(t('common.delete'))}">✕</button>`
          : ''
      }</td>
    </tr>`,
    )
    .join('');

  setContent(`
    <div class="card">
      <p style="font-size:12px;color:var(--text3);margin-bottom:12px">${canWrite ? esc(t('stages.dragdrop.help')) : ''}</p>
      <div class="table-wrap">
        <table id="stages-table">
          <thead><tr><th style="width:32px"></th><th>${esc(t('common.field.name'))}</th><th></th></tr></thead>
          <tbody id="stages-tbody">${rows.length ? rows : `<tr><td colspan="3" class="empty">${esc(t('stages.empty'))}</td></tr>`}</tbody>
        </table>
      </div>
    </div>`);

  if (!canWrite) return;
  initStagesDragDrop(sorted);

  document.getElementById('stages-tbody')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const clickStage = target.closest<HTMLElement>('[data-click-stage]');
    if (clickStage && !target.closest('button')) {
      const id = parseInt(clickStage.dataset['clickStage'] || '0');
      const s = stages.find((x) => x.id === id);
      if (s) showStageModal(s, stages);
      return;
    }
    const editBtn = target.closest<HTMLElement>('[data-edit-stage]');
    if (editBtn) {
      const id = parseInt(editBtn.dataset['editStage'] || '0');
      const s = stages.find((x) => x.id === id);
      if (s) showStageModal(s, stages);
      return;
    }
    const delBtn = target.closest<HTMLElement>('[data-del-stage]');
    if (delBtn) {
      const id = parseInt(delBtn.dataset['delStage'] || '0');
      const name = delBtn.dataset['stageName'] || '';
      openModal(
        t('stages.modal.delete.title', { object: t('vocabulary.stage') }),
        `<p>${t('stages.modal.delete.message', { object: t('vocabulary.stage'), name })}</p>`,
        async () => {
          try {
            await api.deleteStage(id);
            closeModal();
            toast(t('stages.toast.deleted'));
            _cache.stages = null;
            void pages.adminStages?.({});
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('common.error');
            toast(msg, 'error');
          }
        },
        t('common.delete'),
      );
    }
  });
  document
    .getElementById('btn-new-stage')
    ?.addEventListener('click', () => showStageModal(null, stages));
}

function initStagesDragDrop(_stages: ReadonlyArray<StageEntity>): void {
  const tbody = document.getElementById('stages-tbody');
  if (!tbody) return;

  let dragSrc: HTMLElement | null = null;

  tbody.addEventListener('dragstart', (e: DragEvent) => {
    const target = e.target as HTMLElement | null;
    dragSrc = target?.closest<HTMLElement>('tr.stage-drag-row') ?? null;
    if (!dragSrc) return;
    dragSrc.style.opacity = '0.4';
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', dragSrc.dataset['stageId'] || '');
    }
  });

  tbody.addEventListener('dragend', (e: DragEvent) => {
    const target = e.target as HTMLElement | null;
    const row = target?.closest<HTMLElement>('tr.stage-drag-row');
    if (row) row.style.opacity = '';
    tbody.querySelectorAll('tr.stage-drag-row').forEach((r) => r.classList.remove('drag-over'));
  });

  tbody.addEventListener('dragover', (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.stage-drag-row');
    tbody.querySelectorAll('tr.stage-drag-row').forEach((r) => r.classList.remove('drag-over'));
    if (target && target !== dragSrc) target.classList.add('drag-over');
  });

  tbody.addEventListener('dragleave', (e: DragEvent) => {
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.stage-drag-row');
    if (target) target.classList.remove('drag-over');
  });

  tbody.addEventListener('drop', (e: DragEvent) => {
    e.preventDefault();
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.stage-drag-row');
    tbody.querySelectorAll('tr.stage-drag-row').forEach((r) => r.classList.remove('drag-over'));
    if (!target || !dragSrc || target === dragSrc) return;

    const rows = [...tbody.querySelectorAll<HTMLElement>('tr.stage-drag-row')];
    const srcIdx = rows.indexOf(dragSrc);
    const tgtIdx = rows.indexOf(target);
    if (srcIdx < tgtIdx) tbody.insertBefore(dragSrc, target.nextSibling);
    else tbody.insertBefore(dragSrc, target);

    dragSrc.style.opacity = '';

    const newOrder = [...tbody.querySelectorAll<HTMLElement>('tr.stage-drag-row')].map((r) =>
      parseInt(r.dataset['stageId'] || '0'),
    );
    void (async () => {
      try {
        await api.reorderStages(newOrder);
        _cache.stages = null;
        toast(t('stages.toast.reordered'));
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    })();
  });
}

function showStageModal(stage: StageEntity | null, stages: ReadonlyArray<StageEntity>): void {
  const isNew = !stage;
  const body = `
    <div class="form-group"><label>${esc(t('common.field.name'))}</label><input type="text" id="sm-name" value="${esc(stage ? stage.name : '')}" placeholder="${esc(t('stages.field.name.placeholder'))}"></div>`;
  openModal(
    isNew
      ? t('stages.modal.create.title', { object: t('vocabulary.stage') })
      : t('stages.modal.update.title', { object: t('vocabulary.stage') }),
    body,
    async () => {
      const input = document.getElementById('sm-name') as HTMLInputElement | null;
      const name = input?.value.trim() || '';
      if (!name) {
        toast(t('common.error.name_required'), 'error');
        return;
      }
      const pos = isNew
        ? stages.length
          ? Math.max(...stages.map((s) => s.position)) + 1
          : 0
        : (stage?.position ?? 0);
      try {
        if (isNew) await api.createStage({ name, position: pos });
        else if (stage) await api.updateStage(stage.id, { name, position: pos });
        closeModal();
        toast(isNew ? t('stages.toast.created') : t('stages.toast.updated'));
        _cache.stages = null;
        void pages.adminStages?.({});
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('common.add_short') : t('common.save'),
  );
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.renderStagesPage = renderStagesPage;
window.initStagesDragDrop = initStagesDragDrop;
window.showStageModal = showStageModal;

export {};
